import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAccountCommissions,
  fetchSalesRepCommissionById,
  fetchAllCommissions,
  fetchAllRepayments,
  updateBulkCommissionStatusApi,
  updateBulkRepaymentStatusApi,
  fetchRepaymentsBySalesRep,
  updateRepaymentCustomFieldApi,
  updateCommissionApi,
  addRepaymentApi,
  addSalesRepCommissionApi,
  updateRepaymentApi,
  updateBulkCommissionStatusAccountsApi,
} from "../../api/commissionAndRepayment.api";
import {
  ICommissionGroup,
  IStatusCycle,
  ISalesRepCommission,
  IRepayment,
  IRepaymentHistoryItem,
  ICustomField,
} from "../../models/commissionAndRepayment.model";
import { IAddRepaymentData } from "../../models/epcCustomer.model";

interface EpcState {
  salesRepCommissions: ISalesRepCommission[];
  selectedSalesRepCommission: ISalesRepCommission | null;
  selectedSalesRepCommissionGroups: ISalesRepCommission[] | null;
  allApprovedCommissions: {
    groups: ICommissionGroup[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  } | null;
  totalPages: number;
  currentPage: number;

  // Repayments
  repayments: IRepayment[];
  repaymentsTotalPages: number;
  repaymentsCurrentPage: number;
  count: number;
  selectedSalesRepRepayments: {
    repayments: IRepayment[];
    pagination: any;
  } | null;

  loadingState: {
    fetchingCommissionsLoading?: boolean;
    updatingBulkCommissionLoading?: boolean;
    fetchingSalesRepCommissionLoading?: boolean;
    fetchAllApprovedCommissionsLoading?: boolean;
    updateCommissionLoading?: boolean;
    addSalesRepCommissionLoading?: boolean;

    // Repayments loading state
    fetchingRepaymentsLoading?: boolean;
    updatingBulkRepaymentLoading?: boolean;
    updateCustomFieldLoading?: boolean;
    addingRepaymentLoading?: boolean;
    updateRepaymentLoading?: boolean;
  };
  error: string | null;
}

const initialState: EpcState = {
  salesRepCommissions: [],
  selectedSalesRepCommission: null,
  selectedSalesRepCommissionGroups: null,
  allApprovedCommissions: null,
  totalPages: 0,
  currentPage: 1,

  // Repayments
  repayments: [],
  repaymentsTotalPages: 1,
  repaymentsCurrentPage: 1,
  count: 0,
  selectedSalesRepRepayments: null,

  loadingState: {
    fetchingCommissionsLoading: false,
    updatingBulkCommissionLoading: false,
    fetchingSalesRepCommissionLoading: false,
    fetchAllApprovedCommissionsLoading: false,
    updateCommissionLoading: false,
    addSalesRepCommissionLoading: false,
    // Repayments loading state
    fetchingRepaymentsLoading: false,
    updatingBulkRepaymentLoading: false,
    updateCustomFieldLoading: false,
    addingRepaymentLoading: false,
  },
  error: null,
};

export const getAllCommissions = createAsyncThunk(
  "epc/getAllCommissions",
  async (
    params: { page?: number; limit?: number; search?: string; filter?: any },
    { rejectWithValue },
  ) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 20;
      const search = params?.search || "";

      const response = await fetchAllCommissions(
        page,
        limit,
        search,
        params.filter,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch commissions");
    }
  },
);

export const updateBulkCommissions = createAsyncThunk(
  "epc/updateBulkCommissionStatus",
  async (
    {
      commissionHistoryIds,
      action,
      proofFile,
    }: {
      commissionHistoryIds: string[];
      action: "approve" | "reject";
      proofFile?: File | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateBulkCommissionStatusApi(
        commissionHistoryIds,
        action,
        proofFile,
      );
      return { response, action, commissionHistoryIds };
    } catch (error: any) {
      return rejectWithValue(error || `Failed to ${action} bulk commissions`);
    }
  },
);
export const updateBulkCommissionsAccount = createAsyncThunk(
  "epc/updateBulkCommissionStatusAccount",
  async (
    {
      commissionHistoryIds,
      action,
      proofFile,
    }: {
      commissionHistoryIds: string[];
      action: "approve" | "reject";
      proofFile?: File | null;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateBulkCommissionStatusAccountsApi(
        commissionHistoryIds,
        action,
        proofFile,
      );
      return { response, action, commissionHistoryIds };
    } catch (error: any) {
      return rejectWithValue(error || `Failed to ${action} bulk commissions`);
    }
  },
);
export const getSalesRepCommissionById = createAsyncThunk(
  "epc/getSalesRepCommissionById",
  async (
    {
      salesRepId,
      startDate,
      endDate,
    }: {
      salesRepId: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchSalesRepCommissionById(
        salesRepId,
        startDate,
        endDate,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch sales rep commission");
    }
  },
);

export const getAccountCommissions = createAsyncThunk(
  "epc/getAAccountCommissions",
  async (
    params: { page?: number; limit?: number; status: string },
    { rejectWithValue },
  ) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 20;

      const response = await fetchAccountCommissions(
        page,
        limit,
        params.status,
      );

      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch approved commissions");
    }
  },
);

export const getAllRepayments = createAsyncThunk(
  "repayment/getAllRepayments",
  async (
    params: {
      from: string;
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const page = params?.page || 1;
      const limit = params?.limit || 10;
      const search = params?.search || "";
      const status = params?.status || IStatusCycle.PENDING;
      const response = await fetchAllRepayments(
        page,
        limit,
        params.from,
        search,
        status,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch repayments");
    }
  },
);

export const updateBulkRepayments = createAsyncThunk(
  "repayment/updateBulkRepaymentStatus",
  async (
    {
      repaymentHistoryIds,
      action,
      remark,
      proofFile,
      role,
      statusType,
    }: {
      repaymentHistoryIds: string[];
      action: "approve" | "reject";
      remark?: string;
      proofFile?: File | null;
      role: string;
      statusType?: "cse" | "account";
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateBulkRepaymentStatusApi(
        repaymentHistoryIds,
        action,
        remark,
        proofFile,
        statusType,
      );
      return { response, action, repaymentHistoryIds, role, statusType };
    } catch (error: any) {
      return rejectWithValue(error || `Failed to ${action} bulk repayments`);
    }
  },
);

export const getRepaymentsBySalesRep = createAsyncThunk(
  "repayment/getRepaymentsBySalesRep",
  async (
    {
      salesRepId,
      page = 1,
      limit = 20,
      startDate,
      endDate,
      search,
      status,
    }: {
      salesRepId: string;
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
      search?: string;
      status?: IStatusCycle;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchRepaymentsBySalesRep(
        salesRepId,
        page,
        limit,
        startDate,
        endDate,
        search,
        status,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to fetch repayments");
    }
  },
);

export const updateRepaymentCustomField = createAsyncThunk(
  "repayment/updateRepaymentCustomField",
  async (
    {
      repaymentHistoryId,
      customFields,
    }: {
      repaymentHistoryId: string;
      customFields: ICustomField[];
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await updateRepaymentCustomFieldApi(
        repaymentHistoryId,
        customFields,
      );

      dispatch(getAllRepayments({ page: 1, limit: 20, from: "cse" }));
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update custom fields");
    }
  },
);

export const updateCommission = createAsyncThunk(
  "epc/updateCommissionAdjustment",
  async (
    {
      commissionId,
      commissionHistoryId,
      data,
    }: {
      commissionId: string;
      commissionHistoryId: string;
      data: {
        salesRepCommission?: number;
        excessAmount?: number;
        adjustmentAmount?: number;
        excessAdjustAmount?: number;
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateCommissionApi(
        commissionId,
        commissionHistoryId,
        data,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update commission adjustment");
    }
  },
);
export const updateRepayment = createAsyncThunk(
  "epc/updateRepayment",
  async (
    {
      repaymentId,
      repaymentHistoryId,
      data,
    }: {
      repaymentId: string;
      repaymentHistoryId: string;
      data: {
        fileCharge?: number;
        portalCharge?: number;
        customFields?: ICustomField[];
      };
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateRepaymentApi(
        repaymentId,
        repaymentHistoryId,
        data,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to update commission adjustment");
    }
  },
);
export const addRepayment = createAsyncThunk(
  "repayment/addRepayment",
  async (payload: IAddRepaymentData, { rejectWithValue }) => {
    try {
      const response = await addRepaymentApi(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to add repayment");
    }
  },
);

export const addSalesRepCommission = createAsyncThunk(
  "epc/addSalesRepCommission",
  async (
    payload: { salesRepId: string; customerId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await addSalesRepCommissionApi(payload);

      // Refresh the commissions list after adding
      await dispatch(
        getAllCommissions({
          page: 1,
          limit: 20,
          search: "",
        }),
      );

      return response;
    } catch (error: any) {
      return rejectWithValue(error || "Failed to add commission");
    }
  },
);

const commissionAndRepaymentSlice = createSlice({
  name: "commissionAndRepayment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllCommissions.pending, (state) => {
        state.loadingState.fetchingCommissionsLoading = true;
        state.error = null;
      })
      .addCase(getAllCommissions.fulfilled, (state, action) => {
        state.loadingState.fetchingCommissionsLoading = false;
        if (action.payload?.pagination) {
          state.salesRepCommissions = action.payload.commissions || [];
          state.totalPages = action.payload.pagination.totalPages;
          state.currentPage = action.payload.pagination.currentPage;
        } else {
          state.salesRepCommissions =
            action.payload.commissions || action.payload;
          state.totalPages = 1;
        }
      })
      .addCase(getAllCommissions.rejected, (state, action) => {
        state.loadingState.fetchingCommissionsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBulkCommissions.pending, (state) => {
        state.loadingState.updatingBulkCommissionLoading = true;
        state.error = null;
      })
      .addCase(updateBulkCommissions.fulfilled, (state, action) => {
        state.loadingState.updatingBulkCommissionLoading = false;

        const { action: updateAction, commissionHistoryIds } = action.meta.arg;
        const newStatus =
          updateAction === "approve"
            ? IStatusCycle.APPROVED
            : IStatusCycle.REJECTED;

        // Update the local state to reflect the new status
        state.salesRepCommissions = state.salesRepCommissions.map(
          (commission) => {
            const updatedHistory = commission.commissionHistory.map(
              (historyItem) => {
                if (commissionHistoryIds.includes(historyItem._id!)) {
                  return {
                    ...historyItem,
                    status: newStatus,
                  };
                }
                return historyItem;
              },
            );

            return {
              ...commission,
              commissionHistory: updatedHistory,
            };
          },
        );
      })
      .addCase(updateBulkCommissions.rejected, (state, action) => {
        state.loadingState.updatingBulkCommissionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBulkCommissionsAccount.pending, (state) => {
        state.loadingState.updatingBulkCommissionLoading = true;
        state.error = null;
      })
      .addCase(updateBulkCommissionsAccount.fulfilled, (state, action) => {
        state.loadingState.updatingBulkCommissionLoading = false;

        const { action: updateAction, commissionHistoryIds } = action.meta.arg;
        const newStatus =
          updateAction === "approve"
            ? IStatusCycle.APPROVED
            : IStatusCycle.REJECTED;

        // Update the local state to reflect the new status
        state.salesRepCommissions = state.salesRepCommissions.map(
          (commission) => {
            const updatedHistory = commission.commissionHistory.map(
              (historyItem) => {
                if (commissionHistoryIds.includes(historyItem._id!)) {
                  return {
                    ...historyItem,
                    status: newStatus,
                  };
                }
                return historyItem;
              },
            );

            return {
              ...commission,
              commissionHistory: updatedHistory,
            };
          },
        );
      })
      .addCase(updateBulkCommissionsAccount.rejected, (state, action) => {
        state.loadingState.updatingBulkCommissionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getSalesRepCommissionById.pending, (state) => {
        state.loadingState.fetchingSalesRepCommissionLoading = true;
        state.selectedSalesRepCommission = null;
        state.selectedSalesRepCommissionGroups = null;
      })
      .addCase(getSalesRepCommissionById.fulfilled, (state, action) => {
        state.loadingState.fetchingSalesRepCommissionLoading = false;
        state.selectedSalesRepCommission = action.payload;
        if (action.payload && action.payload.groups) {
          state.selectedSalesRepCommissionGroups = action.payload.groups;
        }
      })
      .addCase(getSalesRepCommissionById.rejected, (state, action) => {
        state.loadingState.fetchingSalesRepCommissionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAccountCommissions.pending, (state) => {
        state.loadingState.fetchAllApprovedCommissionsLoading = true;
        state.error = null;
      })
      .addCase(getAccountCommissions.fulfilled, (state, action) => {
        state.loadingState.fetchAllApprovedCommissionsLoading = false;
        state.allApprovedCommissions = action.payload;
      })
      .addCase(getAccountCommissions.rejected, (state, action) => {
        state.loadingState.fetchAllApprovedCommissionsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getAllRepayments.pending, (state) => {
        state.loadingState.fetchingRepaymentsLoading = true;
        state.error = null;
      })
      .addCase(getAllRepayments.fulfilled, (state, action) => {
        state.loadingState.fetchingRepaymentsLoading = false;
        if (action.payload?.pagination) {
          state.repayments = action.payload.repayments || [];
          state.repaymentsTotalPages = action.payload.pagination.totalPages;
          state.repaymentsCurrentPage = action.payload.pagination.currentPage;
          state.count = action.payload.pagination.totalItems;
        } else {
          state.repayments = action.payload.repayments || [];
          state.repaymentsTotalPages = 1;
          state.repaymentsCurrentPage = 1;
          state.count = state.repayments.length;
        }
      })
      .addCase(getAllRepayments.rejected, (state, action) => {
        state.loadingState.fetchingRepaymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateBulkRepayments.pending, (state) => {
        state.loadingState.updatingBulkRepaymentLoading = true;
        state.error = null;
      })
      .addCase(updateBulkRepayments.fulfilled, (state, action) => {
        state.loadingState.updatingBulkRepaymentLoading = false;

        const {
          action: updateAction,
          repaymentHistoryIds,
          role,
        } = action.meta.arg;

        const newStatus =
          updateAction === "approve"
            ? IStatusCycle.APPROVED
            : IStatusCycle.REJECTED;

        state.repayments = state.repayments.map((repayment: IRepayment) => {
          const updatedHistory = repayment.repaymentHistory.map(
            (historyItem: IRepaymentHistoryItem) => {
              if (repaymentHistoryIds.includes(historyItem._id!)) {
                return {
                  ...historyItem,
                  ...(role === "account"
                    ? { accountStatus: newStatus }
                    : { cseStatus: newStatus }),
                };
              }
              return historyItem;
            },
          );

          return {
            ...repayment,
            repaymentHistory: updatedHistory,
          };
        });
      })
      .addCase(updateBulkRepayments.rejected, (state, action) => {
        state.loadingState.updatingBulkRepaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getRepaymentsBySalesRep.pending, (state) => {
        state.loadingState.fetchingRepaymentsLoading = true;
      })
      .addCase(getRepaymentsBySalesRep.fulfilled, (state, action) => {
        state.loadingState.fetchingRepaymentsLoading = false;
        state.selectedSalesRepRepayments = action.payload;
      })
      .addCase(getRepaymentsBySalesRep.rejected, (state, action) => {
        state.loadingState.fetchingRepaymentsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRepaymentCustomField.pending, (state) => {
        state.loadingState.updateCustomFieldLoading = true;
      })
      .addCase(updateRepaymentCustomField.fulfilled, (state, action) => {
        state.loadingState.updateCustomFieldLoading = false;
      })
      .addCase(updateRepaymentCustomField.rejected, (state, action) => {
        state.loadingState.updateCustomFieldLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateCommission.pending, (state) => {
        state.loadingState.updateCommissionLoading = true;
        state.error = null;
      })
      .addCase(updateCommission.fulfilled, (state) => {
        state.loadingState.updateCommissionLoading = false;
      })
      .addCase(updateCommission.rejected, (state, action) => {
        state.loadingState.updateCommissionLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateRepayment.pending, (state) => {
        state.loadingState.updateRepaymentLoading = true;
        state.error = null;
      })
      .addCase(updateRepayment.fulfilled, (state, action) => {
        state.loadingState.updateRepaymentLoading = false;

        const updatedRepayment = action.payload.data;

        state.repayments = state.repayments.map((repayment: IRepayment) =>
          repayment._id === updatedRepayment._id ? updatedRepayment : repayment,
        );

        // also update selectedSalesRepRepayments if loaded
        if (state.selectedSalesRepRepayments?.repayments) {
          state.selectedSalesRepRepayments.repayments =
            state.selectedSalesRepRepayments.repayments.map(
              (repayment: IRepayment) =>
                repayment._id === updatedRepayment._id
                  ? updatedRepayment
                  : repayment,
            );
        }
      })
      .addCase(updateRepayment.rejected, (state, action) => {
        state.loadingState.updateRepaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addRepayment.pending, (state) => {
        state.loadingState.addingRepaymentLoading = true;
        state.error = null;
      })
      .addCase(addRepayment.fulfilled, (state, action) => {
        state.loadingState.addingRepaymentLoading = false;
      })
      .addCase(addRepayment.rejected, (state, action) => {
        state.loadingState.addingRepaymentLoading = false;
        state.error = action.payload as string;
      })
      .addCase(addSalesRepCommission.pending, (state) => {
        state.loadingState.addSalesRepCommissionLoading = true;
        state.error = null;
      })
      .addCase(addSalesRepCommission.fulfilled, (state, action) => {
        state.loadingState.addSalesRepCommissionLoading = false;
      })
      .addCase(addSalesRepCommission.rejected, (state, action) => {
        state.loadingState.addSalesRepCommissionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default commissionAndRepaymentSlice.reducer;
